import * as React from 'react';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../../theme';
import { useBaggageToggleable } from '../../hooks';
import { Baggage } from '../../../BaggageService/types';
import { PassengerLabels } from '../../../Checkout/types';
import { SelectedUpgrade } from '../../IncludedBaggage/types';
import UpgradeBaggageDialog from '../../UpgradeBaggageDialog/components/UpgradeBaggageDialog';
import BaggageCard from '../../BaggageCard';
import { BaggageType } from '@websky/graphql';
import HeaderEquipmentItem from '../../UpgradeBaggageDialog/components/BaggageDialogHeader/HeaderEquipmentItem/HeaderEquipmentItem';
import { getSpecialEquipmentMinPrice } from '../../../Checkout/store/order/selectors';
import { useSelectedServicesBySegments } from '../../UpgradeBaggageDialog/hooks';
import { Segment, Passenger, SelectedBaggageService } from '../../types';
import { useRenderers } from '../../../renderProps';
import cn from 'classnames';
import { BaggageContext } from '../../../Baggage/context';
import BaggageDirection from '../../BaggageList/components/BaggageDirection/BaggageDirection';
import { ServiceMeta } from '../../../Checkout/store/selectedServices/actions';
import { useConfig } from '../../../context';
import BaggageDeleteButton, { hasDeletableServices } from '../../BaggageDeleteButton';
import CdnImage from '../../../CdnImage';

interface Props {
	className?: string;
	passengers: Passenger[];
	passengerLabels: PassengerLabels;
	segments: Segment[];
	services: Baggage[];
	selectedServices: SelectedUpgrade[];
	isLoading?: boolean;
	disabled?: boolean;
	dynamicBaggagePricing?: boolean;
	analyticsMeta?: ServiceMeta;
	onDelete?: (servicesToDelete: SelectedBaggageService[]) => void;
}

const SpecialEquipment: React.FC<Props> = ({
	className,
	passengers,
	passengerLabels,
	segments,
	services,
	selectedServices,
	isLoading,
	disabled,
	dynamicBaggagePricing,
	analyticsMeta,
	onDelete
}) => {
	const { SpecialEquipment: css } = useTheme('SpecialEquipment');
	const { t } = useTranslation('IncludedBaggage');

	const {
		global: {
			servicesAvailable: { animal: showAnimalInSeparateCard }
		}
	} = useConfig();

	const isReadOnly = useContext(BaggageContext)?.isReadOnly;

	const minPrice = useSelector(getSpecialEquipmentMinPrice(showAnimalInSeparateCard));

	const { isOpen, open, close } = useBaggageToggleable(services);

	const { selectedItems, hasSelected, totalPrice } = useSelectedServicesBySegments(
		services,
		segments,
		selectedServices
	);

	const OverrideBaggageCard = useRenderers().Baggage?.SpecialEqupment?.BaggageCard ?? BaggageCard;

	return (
		<>
			<OverrideBaggageCard
				className={cn(className, css.card)}
				classNameIcon={css.card__icon}
				icon={<CdnImage contentRepo path="icons/equipment.svg" />}
				title={t('Equipment')}
				description={t('Need to bring sports, music or baby equipments?')}
				hasSelected={hasSelected}
				fromPrice={minPrice}
				totalPrice={totalPrice}
				onAdd={open}
				onEdit={open}
				readOnly={isReadOnly}
			>
				{hasSelected && (
					<div className={css.flights}>
						{selectedItems.map((selectedService, index) => (
							<div className={css.flight} key={index}>
								<HeaderEquipmentItem
									className={css.flight_icon}
									rfics={selectedService.service.rfics}
									baggageType={selectedService.service.type}
									isActive
								/>
								<div className={css.flight_count}>x{selectedService.count}</div>
								<span className={css.flight_info}>
									{selectedService.service.name},{' '}
									<BaggageDirection
										className={css.flight_direction}
										directions={selectedService.directions}
										allSegments={selectedService.allSegments}
									/>
								</span>
								{onDelete && hasDeletableServices(selectedService.selectedServices) && (
									<BaggageDeleteButton
										className={css.flight_deleteButton}
										onClick={() => onDelete(selectedService.selectedServices)}
									/>
								)}
							</div>
						))}
					</div>
				)}
			</OverrideBaggageCard>

			<UpgradeBaggageDialog
				className={className}
				open={isOpen}
				onClose={close}
				passengers={passengers}
				passengerLabels={passengerLabels}
				segments={segments}
				includedBaggage={[]}
				upgradeBaggage={services}
				selectedServices={selectedServices}
				headerProps={{
					header: t('Equipment'),
					description: ''
				}}
				disabled={isLoading || disabled}
				baggageType={BaggageType.SportingEquipment}
				dynamicBaggagePricing={dynamicBaggagePricing}
				isLoading={isLoading}
				analyticsMeta={analyticsMeta}
				valueAlign="right"
			/>
		</>
	);
};

export default SpecialEquipment;
